import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, User, Wrench, Plus, X, CheckCircle } from 'lucide-react';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'job' | 'appointment' | 'visit' | 'maintenance' | 'meeting';
  date: string;
  time: string;
  endTime?: string;
  technician?: string;
  customer?: string;
  location?: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

const typeColors: Record<string, { bg: string; text: string; dot: string }> = {
  job: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  appointment: { bg: 'bg-violet-100', text: 'text-violet-700', dot: 'bg-violet-500' },
  visit: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-500' },
  maintenance: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  meeting: { bg: 'bg-pink-100', text: 'text-pink-700', dot: 'bg-pink-500' },
};

const mockEvents: CalendarEvent[] = [
  { id: 'E1', title: 'CCTV Install — AIA Insurance', type: 'job', date: '2026-07-10', time: '9:00', endTime: '13:00', technician: 'Kamal Perera', customer: 'AIA Insurance', location: 'Colombo 3', status: 'in-progress' },
  { id: 'E2', title: 'Network Setup — Hotel Reef', type: 'job', date: '2026-07-10', time: '11:00', endTime: '16:00', technician: 'Nimal Silva', customer: 'Hotel Reef', location: 'Negombo', status: 'scheduled' },
  { id: 'E3', title: 'Site Visit — Lanka Hospitals', type: 'visit', date: '2026-07-11', time: '10:00', endTime: '11:30', technician: 'Dilshan Fernando', customer: 'Lanka Hospitals', location: 'Colombo 5', status: 'scheduled' },
  { id: 'E4', title: 'Alarm Maintenance — Sampath Bank', type: 'maintenance', date: '2026-07-11', time: '14:00', endTime: '16:00', technician: 'Suresh Kumar', customer: 'Sampath Bank', location: 'Kandy', status: 'scheduled' },
  { id: 'E5', title: 'Client Meeting — MAS Holdings', type: 'meeting', date: '2026-07-12', time: '9:00', endTime: '10:00', customer: 'MAS Holdings', location: 'Office', status: 'scheduled' },
  { id: 'E6', title: 'Starlink Install — Dialog Axiata', type: 'job', date: '2026-07-12', time: '8:00', endTime: '11:00', technician: 'Kamal Perera', customer: 'Dialog Axiata', location: 'Maradana', status: 'scheduled' },
  { id: 'E7', title: 'Annual Service — AIA Insurance', type: 'maintenance', date: '2026-07-15', time: '10:00', endTime: '12:00', technician: 'Nimal Silva', customer: 'AIA Insurance', location: 'Colombo 3', status: 'scheduled' },
  { id: 'E8', title: 'Site Survey — New Hotel Project', type: 'visit', date: '2026-07-16', time: '9:00', endTime: '10:30', technician: 'Kamal Perera', customer: 'Potential Client', location: 'Hikkaduwa', status: 'scheduled' },
  { id: 'E9', title: 'WiFi Setup — Cinnamon Grand', type: 'job', date: '2026-07-17', time: '8:00', endTime: '17:00', technician: 'Suresh Kumar', customer: 'Cinnamon Grand', location: 'Colombo 2', status: 'scheduled' },
  { id: 'E10', title: 'Customer Appointment — DFCC Bank', type: 'appointment', date: '2026-07-18', time: '14:00', endTime: '15:00', customer: 'DFCC Bank', location: 'Nawam Mawatha', status: 'scheduled' },
];

const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const hours = Array.from({ length: 10 }, (_, i) => i + 8); // 8am to 5pm

function getCalendarDays(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(firstDay).fill(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);
  return days;
}

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 6));
  const [view, setView] = useState<'month' | 'week'>('month');
  const [selectedDay, setSelectedDay] = useState<number | null>(10);
  const [events, setEvents] = useState<CalendarEvent[]>(mockEvents);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  const days = getCalendarDays(currentDate.getFullYear(), currentDate.getMonth());
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const getEventsForDay = (day: number) => events.filter(e => {
    const d = new Date(e.date);
    return d.getDate() === day && d.getMonth() === currentDate.getMonth();
  });

  const selectedDayEvents = selectedDay ? getEventsForDay(selectedDay) : [];

  const daysWithEvents = events
    .filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === currentDate.getMonth();
    })
    .map(e => new Date(e.date).getDate());

  return (
    <div className="h-full overflow-y-auto bg-[#FAFAFA] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-teal-200">
            <Calendar className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Calendar</h1>
            <p className="text-xs text-gray-500">Jobs · Appointments · Site Visits · Maintenance</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">
            {(['month', 'week'] as const).map(v => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-2 text-xs font-medium transition-colors capitalize ${view === v ? 'bg-teal-500 text-white' : 'text-gray-500 hover:text-gray-700'}`}>{v}</button>
            ))}
          </div>
          <button className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-xs font-medium px-3 py-2 rounded-xl hover:shadow-teal-200 hover:shadow-lg transition-all">
            <Plus className="w-3.5 h-3.5" /> Add Event
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-4 flex-wrap">
        {Object.entries(typeColors).map(([type, colors]) => (
          <div key={type} className="flex items-center gap-1.5 text-xs text-gray-600 capitalize">
            <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
            {type}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Month Calendar */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
            <span className="text-sm font-semibold text-gray-900">{monthName}</span>
            <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>

          {/* Week headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {weekDays.map(d => <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>)}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((day, i) => {
              const dayEvents = day ? getEventsForDay(day) : [];
              const isSelected = selectedDay === day;
              const isToday = day === 10; // Mock today
              return (
                <button
                  key={i}
                  onClick={() => day && setSelectedDay(day)}
                  disabled={!day}
                  className={`min-h-[56px] rounded-xl p-1 transition-all text-left ${
                    !day ? '' :
                    isSelected ? 'bg-teal-500' :
                    isToday ? 'bg-teal-50 border-2 border-teal-200' :
                    'hover:bg-gray-50'
                  }`}
                >
                  {day && (
                    <>
                      <div className={`text-xs font-semibold mb-1 ${isSelected ? 'text-white' : isToday ? 'text-teal-700' : 'text-gray-700'}`}>
                        {day}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 2).map(e => (
                          <div
                            key={e.id}
                            className={`text-[9px] px-1 py-0.5 rounded-sm truncate font-medium ${isSelected ? 'bg-white/20 text-white' : `${typeColors[e.type].bg} ${typeColors[e.type].text}`}`}
                            onClick={ev => { ev.stopPropagation(); setSelectedEvent(e); }}
                          >
                            {e.time} {e.title.split('—')[0].trim()}
                          </div>
                        ))}
                        {dayEvents.length > 2 && (
                          <div className={`text-[9px] font-medium ${isSelected ? 'text-white/80' : 'text-gray-400'}`}>+{dayEvents.length - 2} more</div>
                        )}
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Day Events */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              {selectedDay ? `July ${selectedDay}` : 'Select a date'}
            </h2>
            <span className="text-xs text-gray-500">{selectedDayEvents.length} events</span>
          </div>

          {selectedDayEvents.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No events</p>
            </div>
          ) : (
            selectedDayEvents.map(event => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => setSelectedEvent(event)}
                className="bg-white rounded-2xl border border-gray-200 p-4 cursor-pointer hover:shadow-sm transition-shadow"
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-2 h-full min-h-[40px] rounded-full ${typeColors[event.type].dot} flex-shrink-0 mt-0.5`} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${typeColors[event.type].bg} ${typeColors[event.type].text} capitalize`}>
                        {event.type}
                      </span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        event.status === 'in-progress' ? 'bg-green-100 text-green-700' :
                        event.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                        event.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{event.status}</span>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 mt-1">{event.title}</p>
                    <div className="space-y-0.5 mt-1.5">
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Clock className="w-3 h-3" /> {event.time}{event.endTime ? ` – ${event.endTime}` : ''}
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <MapPin className="w-3 h-3" /> {event.location}
                        </div>
                      )}
                      {event.technician && (
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <Wrench className="w-3 h-3" /> {event.technician}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}

          {/* Upcoming summary */}
          <div className="bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl p-4 text-white">
            <h3 className="text-xs font-semibold mb-2">This Week Summary</h3>
            {[['Total Events', events.length], ['Jobs', events.filter(e => e.type === 'job').length], ['Maintenance', events.filter(e => e.type === 'maintenance').length]].map(([l, v]) => (
              <div key={l as string} className="flex justify-between text-xs py-1 border-b border-white/10 last:border-0">
                <span className="opacity-80">{l}</span>
                <span className="font-bold">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedEvent(null)}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} onClick={e => e.stopPropagation()} className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <span className={`text-xs font-semibold px-2 py-1 rounded-full capitalize ${typeColors[selectedEvent.type].bg} ${typeColors[selectedEvent.type].text}`}>{selectedEvent.type}</span>
              <button onClick={() => setSelectedEvent(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"><X className="w-4 h-4 text-gray-600" /></button>
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-3">{selectedEvent.title}</h3>
            <div className="space-y-2">
              {[
                { icon: Clock, label: `${selectedEvent.date} · ${selectedEvent.time}${selectedEvent.endTime ? ` – ${selectedEvent.endTime}` : ''}` },
                selectedEvent.location ? { icon: MapPin, label: selectedEvent.location } : null,
                selectedEvent.technician ? { icon: Wrench, label: selectedEvent.technician } : null,
                selectedEvent.customer ? { icon: User, label: selectedEvent.customer } : null,
              ].filter(Boolean).map((item, i) => {
                const Icon = item!.icon;
                return (
                  <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon className="w-4 h-4 text-gray-400" />
                    {item!.label}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
